(() => {
  if (window.ThemeSearchPagination) {
    window.ThemeSearchPagination.init(document);
    return;
  }

  function collect(scope, selector) {
    const nodes = [];

    if (scope instanceof Element && scope.matches(selector)) {
      nodes.push(scope);
    }

    if (scope?.querySelectorAll) {
      nodes.push(...scope.querySelectorAll(selector));
    }

    return nodes;
  }

  function announce(message) {
    if (window.PegasusTheme?.announce) {
      window.PegasusTheme.announce(message);
      return;
    }

    const status = document.querySelector('[data-theme-status]');
    if (status) status.textContent = message;
  }

  function productOnlyUrl(targetUrl) {
    const requestUrl = new URL(targetUrl, window.location.href);
    requestUrl.searchParams.set('type', 'product');
    return requestUrl;
  }

  function ensureProductOnlyPage() {
    const currentUrl = new URL(window.location.href);

    if (!currentUrl.searchParams.has('q') || currentUrl.searchParams.get('type') === 'product') {
      return false;
    }

    window.location.replace(productOnlyUrl(currentUrl).toString());
    return true;
  }

  async function fetchPage(targetUrl) {
    const requestUrl = productOnlyUrl(targetUrl);
    const response = await fetch(requestUrl.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to load more products.');
    }

    return {
      markup: await response.text(),
      url: response.url || requestUrl.toString(),
    };
  }

  async function loadMoreProducts(section, button) {
    const sectionId = String(section?.dataset?.searchSectionId || '').trim();
    const grid = section?.querySelector('[data-search-grid]');
    const currentPagination = button?.closest('[data-search-pagination]');
    const targetUrl = button?.href;

    if (!sectionId || !grid || !currentPagination || !targetUrl) {
      throw new Error('Unable to load more products.');
    }

    const label = button.querySelector('[data-load-more-label]');
    const originalLabel = label?.textContent || '';

    button.dataset.loading = 'true';
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');

    if (label) {
      label.textContent = button.dataset.loadingLabel || 'Loading…';
    }

    try {
      const pageResponse = await fetchPage(targetUrl);
      const parsedDocument = new DOMParser().parseFromString(pageResponse.markup, 'text/html');
      const nextSection = collect(parsedDocument, '[data-search-section-id]').find((candidate) => (
        candidate.dataset.searchSectionId === sectionId
      ));
      const incomingItems = nextSection ? collect(nextSection, '[data-search-result]') : [];
      const incomingPagination = nextSection?.querySelector('[data-search-pagination]');
      const initialTotal = Number(currentPagination.dataset.totalProducts) || 0;

      if (!nextSection || !incomingItems.length) {
        throw new Error('Unable to load more products.');
      }

      incomingItems.forEach((item) => grid.append(item));

      const loadedProductCount = grid.querySelectorAll('[data-search-result]').length;

      if (incomingPagination) {
        const totalProductCount = Math.max(
          loadedProductCount,
          initialTotal || Number(incomingPagination.dataset.totalProducts) || loadedProductCount,
        );
        const progressLabel = incomingPagination.querySelector('[data-search-progress-label]');
        const progressBar = incomingPagination.querySelector('.collection__load-more-bar');

        incomingPagination.dataset.totalProducts = String(totalProductCount);
        if (progressLabel) progressLabel.textContent = `Showing ${loadedProductCount} of ${totalProductCount}`;
        if (progressBar) {
          progressBar.style.width = `${Math.min(100, (loadedProductCount / totalProductCount) * 100)}%`;
        }

        currentPagination.replaceWith(incomingPagination);
      } else {
        currentPagination.remove();
      }

      window.history.pushState(
        { ...window.history.state, searchLoadMore: true },
        '',
        pageResponse.url,
      );

      grid.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId },
      }));

      announce(`${loadedProductCount} products loaded.`);
    } catch (error) {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      delete button.dataset.loading;

      if (label) label.textContent = originalLabel;

      throw error;
    }
  }

  async function replaceSearchSection(currentSection, targetUrl) {
    const sectionId = String(currentSection?.dataset?.searchSectionId || '').trim();

    if (!sectionId || !targetUrl) {
      throw new Error('Unable to update search results.');
    }

    const pageResponse = await fetchPage(targetUrl);
    const parsedDocument = new DOMParser().parseFromString(pageResponse.markup, 'text/html');
    const replacement = collect(parsedDocument, '[data-search-section-id]').find((candidate) => (
      candidate.dataset.searchSectionId === sectionId
    ));

    if (!replacement) {
      throw new Error('Unable to update search results.');
    }

    currentSection.replaceWith(replacement);
    replacement.dispatchEvent(new CustomEvent('shopify:section:load', {
      bubbles: true,
      detail: { sectionId },
    }));
  }

  function init(scope = document) {
    collect(scope, '[data-search-section-id]').forEach((section) => {
      if (section.dataset.searchPaginationInitialized === 'true') return;
      section.dataset.searchPaginationInitialized = 'true';

      section.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const loadMoreButton = event.target.closest('[data-search-load-more]');

        if (!loadMoreButton || !section.contains(loadMoreButton)) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        event.preventDefault();

        if (loadMoreButton.dataset.loading === 'true') return;

        const targetUrl = loadMoreButton.href;

        loadMoreProducts(section, loadMoreButton).catch(() => {
          window.location.assign(productOnlyUrl(targetUrl).toString());
        });
      });
    });
  }

  window.ThemeSearchPagination = { init };

  if (ensureProductOnlyPage()) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  window.addEventListener('popstate', () => {
    const section = document.querySelector('[data-search-section-id]');

    if (!section) return;

    replaceSearchSection(section, window.location.href).catch(() => {
      window.location.reload();
    });
  });
})();
